<template>
  <div class="full_page">

    <h1>Vegas Accounts</h1>
    <div class="sub" v-if="header">
      {{ storeCount }} stores from the Vegas buyer retreats · as of {{ todayLabel }}
    </div>

    <div v-if="multi_show_data_loading" class="loading">Loading…</div>

    <template v-else-if="header">

      <!-- KPI row -->
      <div class="kpis">
        <div class="kpi"><div class="l">Stores</div><div class="v">{{ header.number_of_stores }}</div></div>
        <div class="kpi"><div class="l">Trips</div><div class="v">{{ header.trips }}</div></div>
        <div class="kpi"><div class="l">Avg Sales / Mo</div><div class="v">{{ header.avg_sales_mo }}</div></div>
        <div class="kpi"><div class="l">Ordered Last 30d</div><div class="v">{{ header.ordered_last_30d }}</div></div>
        <div class="kpi"><div class="l">Lapsed 60d+</div><div class="v">{{ header.lapsed60dayplus }}</div></div>
        <div class="kpi"><div class="l">Orders</div><div class="v">{{ header.number_of_orders }}</div></div>
        <div class="kpi"><div class="l">Qty Total</div><div class="v">{{ header.total_qty }}</div></div>
        <div class="kpi"><div class="l">AR Total</div><div class="v">{{ header.total_ar }}</div></div>
        <div class="kpi"><div class="l">LFB Rewards</div><div class="v">{{ header.LFB_rewards }}</div></div>
      </div>

      <!-- GROK INSERT Graph here -->
      <div class="graph_background" v-if="multi_show_data && multi_show_data.graph_data && multi_show_data.graph_data.amount">
        <line-chart
            ytitle="Sales $"
            prefix="$ "
            :dataset="{borderWidth: 3}"
            :colors="['#4cd9ae', '#1500e8', '#000000']"
            :data="multi_show_data.graph_data.amount">
        </line-chart>
      </div>

      <!-- Trip tabs + status legend -->
      <div class="controls">
        <div class="tabs">
          <button class="tab" :class="{ on: tripFilter === 'all' }" @click="tripFilter = 'all'">
            All stores ({{ allStores.length }})
          </button>
          <button class="tab" :class="{ on: tripFilter === '1' }" @click="tripFilter = '1'">
            Trip 1 ({{ trip1Count }})
          </button>
          <button class="tab" :class="{ on: tripFilter === '2' }" @click="tripFilter = '2'">
            Trip 2 ({{ trip2Count }})
          </button>
        </div>
        <div class="status-legend">
          <span><i class="dot active"></i>Active — ≤ 30 days</span>
          <span><i class="dot slowing"></i>Slowing — 31–60 days</span>
          <span><i class="dot lapsed"></i>Lapsed — > 60 days</span>
        </div>
      </div>

      <!-- Stores table -->
      <div class="scroll">
        <div class="table">
          <div class="cols thead">
            <div class="th cell num-col" @click="sortBy('trip_shop_number')">#</div>
            <div class="th cell name" @click="sortBy('store')">Store</div>
            <div class="th cell">Customer ID</div>
            <div class="th cell" @click="sortBy('trip')">Trip</div>
            <div class="th cell" @click="sortBy('days_since')">Status</div>
            <div class="th cell" @click="sortBy('first_order_date')">First Order</div>
            <div class="th cell" @click="sortBy('last_order_date')">Last Order</div>
            <div class="th cell num" @click="sortBy('days_since')">Days Since</div>
            <div class="th cell num" @click="sortBy('orders_count')">Orders</div>
            <div class="th cell num" @click="sortBy('qty_total')">Qty Total</div>
            <div class="th cell num" @click="sortBy('avg_sales_mo')">Avg Sales / Mo</div>
            <div class="th cell num" @click="sortBy('ar_total')">AR Total</div>
            <div class="th cell num" @click="sortBy('LFB_rewards_earned')">LFB Rewards</div>
            <div class="th cell num" @click="sortBy('LFB_rewards_remaining')">LFB Remaining</div>
            <div class="th cell"></div>
          </div>

          <div v-for="store in filteredStores" :key="storeKey(store)" class="store-wrap">
            <div class="row cols" :class="{ open: expandedKey === storeKey(store) }" @click="toggleStore(store)">
              <div class="cell num-col">{{ store.trip_shop_number }}</div>
              <div class="cell name" :title="store.store">{{ store.store }}</div>
              <div class="cell id" :title="store.customer_id">{{ store.customer_id }}</div>
              <div class="cell"><span class="trip" :class="'t' + store.trip">Trip {{ store.trip }}</span></div>
              <div class="cell st">
                <i class="dot" :class="colorClass(store.active_color)"></i>
                {{ colorLabel(store.active_color) }}
              </div>
              <div class="cell">{{ store.first_order_date }}</div>
              <div class="cell">{{ store.last_order_date }}</div>
              <div class="cell num">{{ store.days_since }}</div>
              <div class="cell num">{{ store.orders_count }}</div>
              <div class="cell num">{{ store.qty_total }}</div>
              <div class="cell num">{{ formatNumber(store.avg_sales_mo) }}</div>
              <div class="cell num">{{ formatNumber(store.ar_total) }}</div>
              <div class="cell num">{{ formatNumber(store.LFB_rewards_earned) }}</div>
              <div class="cell num">{{ formatNumber(store.LFB_rewards_remaining) }}</div>
              <div class="cell arrow">{{ expandedKey === storeKey(store) ? '▴' : '▾' }}</div>
            </div>

            <!-- Expanded detail -->
            <div v-if="expandedKey === storeKey(store)" class="detail">
              <div class="detail-title">
                <b>Accounts — {{ store.store }}</b>
                <small>{{ (store.account_details || []).length }} customer ID(s)</small>
              </div>

              <div class="otable" v-if="store.account_details && store.account_details.length">
                <div class="acols ohead">
                  <div class="cell">Customer ID</div>
                  <div class="cell name bold">Account name</div>
                  <div class="cell">First Order</div>
                  <div class="cell">Last Order</div>
                  <div class="cell num">Days</div>
                  <div class="cell num">Orders</div>
                  <div class="cell num">Qty Total</div>
                  <div class="cell num">Avg Sales / Mo</div>
                  <div class="cell num">AR Total</div>
                  <div class="cell num">LFB Rewards</div>
                  <div class="cell num">LFB Remaining</div>
                  <div class="cell name bold">Never Ordered</div>
                </div>
                <div v-for="(acc, j) in store.account_details" :key="acc.customer_id" class="acols orow" :class="{ alt: j % 2 }">
                  <div class="cell id" :title="acc.customer_id">{{ acc.customer_id }}</div>
                  <div class="cell name bold" :title="acc.account_name">{{ acc.account_name }}</div>
                  <div class="cell">{{ formatDate(acc.first_order_date) }}</div>
                  <div class="cell">{{ formatDate(acc.last_order_date) }}</div>
                  <div class="cell num st">
                    <i class="dot" :class="colorClass(acc.Days_since_last_order_color)"></i>
                    {{ acc.Days_since_last_order || '—' }}
                  </div>
                  <div class="cell num">{{ acc.orders_count || '—' }}</div>
                  <div class="cell num">{{ formatNumber(acc.units_total) }}</div>
                  <div class="cell num">{{ formatNumber(acc.avg_sales_mo) }}</div>
                  <div class="cell num">{{ formatNumber(acc.total_ar_due) }}</div>
                  <div class="cell num">{{ formatNumber(acc.lfb_reward_point) }}</div>
                  <div class="cell num">{{ formatNumber(acc.lfb_remaining_point) }}</div>
                  <div class="cell left" :title="acc.never_ordered_string">{{ acc.never_ordered_string || '—' }}</div>
                </div>
              </div>

              <div class="detail-title" style="margin-top:14px">
                <b>Order history</b>
                <small>newest first</small>
              </div>

              <div class="otable" v-if="store.order_history && store.order_history.data">
                <div class="ocols ohead">
                  <div class="cell">Order Date</div>
                  <div class="cell">Ship Date</div>
                  <div class="cell num">Total $</div>
                  <div v-for="(label, key) in store.order_history.fields_array" :key="key" class="cell num">{{ label }}</div>
                </div>
                <div v-for="(order, k) in store.order_history.data" :key="k" class="ocols orow" :class="{ alt: k % 2 }">
                  <div class="cell">{{ order.order_date }}</div>
                  <div class="cell">{{ order.ship_date }}</div>
                  <div class="cell num">{{ order.order_total }}</div>
                  <div v-for="(label, key) in store.order_history.fields_array" :key="key" class="cell num">{{ order[key] || 0 }}</div>
                </div>
              </div>
              <div v-else class="ph">No order history</div>
            </div>
          </div>
        </div>
      </div>

    </template>

    <div v-else class="ph">No data</div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  data() {
    return {
      tripFilter: 'all',
      sortKey: null,
      sortDir: 1,
      expandedKey: null
    }
  },
  computed: {
    ...mapGetters({
      multi_show_data: 'admin/multi_show_data',
      multi_show_data_loading: 'admin/multi_show_data_loading',
    }),
    header() {
      return this.multi_show_data && this.multi_show_data.shop_top_data_header
    },
    allStores() {
      const detail = this.multi_show_data && this.multi_show_data.shop_trips_detail
      if (!detail || !detail.trips) return []
      const stores = []
      Object.keys(detail.trips).sort().forEach(tripId => {
        const arr = detail.trips[tripId].top_store_array || []
        arr.forEach(s => stores.push(s))
      })
      return stores
    },
    trip1Count() {
      return this.allStores.filter(s => String(s.trip) === '1').length
    },
    trip2Count() {
      return this.allStores.filter(s => String(s.trip) === '2').length
    },
    storeCount() {
      return this.allStores.length
    },
    filteredStores() {
      let list = this.allStores.slice()
      if (this.tripFilter !== 'all') {
        list = list.filter(s => String(s.trip) === this.tripFilter)
      }
      if (this.sortKey) {
        const key = this.sortKey
        const dir = this.sortDir
        list.sort((a, b) => {
          let va = a[key]
          let vb = b[key]
          if (typeof va === 'string') return va.localeCompare(vb) * dir
          va = Number(va) || 0
          vb = Number(vb) || 0
          return (va - vb) * dir
        })
      }
      return list
    },
    todayLabel() {
      return new Date().toLocaleDateString('en-US')
    }
  },
  mounted() {
    this.GetMultiShopData()
  },
  methods: {
    GetMultiShopData() {
      this.$store.dispatch('admin/getMultiShop', { 'multi_shop_name': 'vegas' })
    },
    storeKey(store) {
      return store.trip + '-' + store.trip_shop_number + '-' + store.customer_id
    },
    toggleStore(store) {
      const key = this.storeKey(store)
      this.expandedKey = this.expandedKey === key ? null : key
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDir = -this.sortDir
      } else {
        this.sortKey = key
        this.sortDir = 1
      }
    },
    colorClass(color) {
      const c = (color || '').toLowerCase()
      if (c === 'green') return 'active'
      if (c === 'yellow') return 'slowing'
      if (c === 'red') return 'lapsed'
      return ''
    },
    colorLabel(color) {
      const c = (color || '').toLowerCase()
      if (c === 'green') return 'Active'
      if (c === 'yellow') return 'Slowing'
      if (c === 'red') return 'Lapsed'
      return '—'
    },
    formatNumber(n) {
      if (n == null || n === '') return '—'
      return Number(n).toLocaleString('en-US')
    },
    formatDate(d) {
      if (!d) return '—'
      return String(d).substring(0, 10)
    }
  }
}
</script>

<style scoped>
.full_page {
  margin: 0 auto;
  max-width: 1800px;
  padding: 14px 8px 56px;
  font-family: 'Avenir Next', Avenir, Nunito, 'Segoe UI', sans-serif;
  font-size: 14px;
  color: #222;
  background: #fff;
}

h1 {
  margin: 0;
  font-size: 44px;
  font-weight: 500;
  color: #2d4059;
  text-align: center;
}

.sub {
  font-size: 13px;
  color: #666;
  text-align: center;
  margin-bottom: 18px;
}

.loading, .ph {
  text-align: center;
  color: #888;
  padding: 40px;
  font-style: italic;
}

.kpis {
  display: grid;
  grid-template-columns: repeat(9, minmax(0, 1fr));
  width: 100%;
  margin-bottom: 18px;
}

.kpi {
  border: 1px solid #dedede;
  padding: 14px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.kpi + .kpi { margin-left: -1px; }

.kpi .l {
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
}

.kpi .v {
  font-size: 16px;
  white-space: nowrap;
}

.controls {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.tabs {
  display: flex;
  border: 1px solid #c9d1dc;
}

.tab {
  font: inherit;
  cursor: pointer;
  border: 0;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  min-height: 44px;
  background: #fff;
  color: #2d4059;
}

.tab + .tab { border-left: 1px solid #c9d1dc; }
.tab.on { background: #2d4059; color: #fff; }

.status-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px;
  font-size: 12px;
  color: #444;
}

.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 4px;
}
.dot.active { background: #2e9e4f; }
.dot.slowing { background: #d99a00; }
.dot.lapsed { background: #d32f2f; }

.scroll { width: 100%; overflow-x: auto; }

.table {
  min-width: 1500px;
  border: 1px solid #dedede;
}

.cols {
  display: grid;
  grid-template-columns: 60px minmax(220px, 1.6fr) 160px 100px 120px 120px 120px 100px 90px 110px 130px 110px 140px 140px 40px;
  align-items: center;
  width: 100%;
}

.acols {
  display: grid;
  grid-template-columns: 120px minmax(220px, 1.4fr) 110px 110px 80px 70px 90px 110px 90px 110px 120px minmax(220px, 1.5fr);
  align-items: center;
  width: 100%;
}

.ocols {
  display: grid;
  grid-template-columns: 100px 100px 90px repeat(auto-fit, minmax(60px, 1fr));
  align-items: center;
  width: 100%;
}

.cell {
  padding: 0 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.cell.num-col {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}

.cell.num {
  justify-content: flex-end;
  font-variant-numeric: tabular-nums;
  padding-right: 14px;
}

.cell.left {
  justify-content: flex-start;
  font-weight: 400;
  text-align: left;
}

.th, .row {
  font: inherit;
  color: inherit;
  cursor: pointer;
  border: 0;
  background: none;
  padding: 0;
}

.thead {
  border-bottom: 1px solid #dedede;
  min-height: 48px;
  background: #f6f8fa;
  align-items: center;
}

.th {
  min-height: 48px;
  font-weight: 700;
  font-size: 13px;
  color: #2d4059;
  display: flex;
  align-items: center;
}

.th:hover { color: #1a2a3a; }

.row {
  min-height: 50px;
  border-bottom: 1px solid #e6e6e6;
  align-items: center;
}

.row:hover { background: #e9eef4 !important; }
.row.open { background: #e3eaf2; }

.name {
  text-align: left;
  font-weight: 600;
  justify-content: flex-start;
}
.name.bold { font-weight: 700; }
.id { font-size: 13px; color: #555; font-variant-numeric: tabular-nums; }

.trip {
  display: inline-block;
  padding: 3px 9px;
  font-size: 12px;
  font-weight: 600;
}
.trip.t1 { background: #eef1f5; color: #2d4059; border: 1px solid #c9d1dc; }
.trip.t2 { background: #2d4059; color: #fff; }

.st { display: inline-flex; align-items: center; justify-content: center; gap: 6px; }

.detail {
  background: #fafbfc;
  border-top: 1px solid #dedede;
  padding: 14px 16px 18px 40px;
}

.detail-title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 14px;
  margin-bottom: 8px;
}
.detail-title b { font-size: 14px; color: #2d4059; }
.detail-title small { font-size: 12px; color: #666; }

.otable {
  border: 1px solid #dedede;
  background: #fff;
  margin-bottom: 8px;
}

.ohead {
  min-height: 36px;
  font-weight: 700;
  font-size: 12px;
  border-bottom: 1px solid #dedede;
  background: #f6f8fa;
  align-items: center;
}

.orow {
  min-height: 34px;
  font-size: 13px;
  align-items: center;
}
.orow.alt { background: #f6f6f6; }

.arrow { color: #888; font-size: 12px; }

.graph_background {
  border-radius: 7px;
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 10px;
  background: #FFF;
}
</style>
